import { useState, useEffect, useRef, useMemo, RefObject } from "react";
import { useDispatch } from "react-redux";
import { FilterOption } from "types";

import {
  setTemporaryAssignedTags,
  addNewTagFromCart,
  setAssignedTags,
  updateExportTags,
} from "redux/SingleDataset/actions";

import classNames from "classnames";
import SearchIcon from "assets/icons/SearchIcon";
import { FaPlus } from "react-icons/fa";
import styles from "./style.module.scss";
import XIcon from "assets/icons/XIcon";
import VLButton from "views/uikit/Buttons/VLButton";
import VLCheckBox from "views/uikit/VLCheckbox";

const SingleTagTag = ({ tag, handleRemoval }: SingleTagTagProps) => {
  const onRemoveButtonClick = (
    e: React.MouseEvent<HTMLDivElement>,
    tag: FilterOption,
  ) => {
    e.stopPropagation();
    handleRemoval(tag);
  };
  return (
    <div className={styles.tagTagContainer}>
      <span className={styles.tagTagText}>{tag.text}</span>
      <div
        className={styles.tagRemoveButton}
        onClick={(e: React.MouseEvent<HTMLDivElement>) =>
          onRemoveButtonClick(e, tag)
        }
      >
        <XIcon />
      </div>
    </div>
  );
};

const SingleTagRow = ({ tag, onTagAddition, selected }: SingleTagProps) => {
  const containerClassnames = classNames(styles.tagContainer, {
    [styles.selectedContainer]: selected,
  });

  const handleSelection = (
    e: React.MouseEvent<HTMLDivElement>,
    tag: FilterOption,
  ) => {
    e.stopPropagation();
    onTagAddition(tag);
  };

  return (
    <div
      className={containerClassnames}
      onClick={(e: any) => handleSelection(e, tag)}
    >
      <VLCheckBox checked={selected} />
      <label className={styles.text} htmlFor={tag.text}>
        {tag.text}
      </label>
    </div>
  );
};

const TagsDropDown = ({
  assignedTags,
  allTags,
  toggleShowTags,
  btnRef,
  onTagSubmission,
  onTagRemoval,
  datasetID,
  caller = "cart",
}: TagsDropDownProps) => {
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState("");
  const [searchedResults, setSearchedResults] = useState<any[]>([]);
  const [newTag, setNewTag] = useState("");
  const [currentSelectedTags, setCurrentSelectedTags] = useState<
    FilterOption[]
  >([]);

  const tagsContainerStyle = classNames(styles.tagsContainer, {
    [styles.maxHeight]: !assignedTags.length,
  });

  const showApplybutton = currentSelectedTags.length > 0;
  const tagsDropdownRef = useRef<HTMLDivElement>(null);
  const trimmedSearchText = searchText.replace(/^\s+|\s+$/g, "");
  const nonAssignedTags = useMemo(() => {
    return allTags.filter(
      (tag) => !assignedTags.some((assignedTag) => assignedTag.id === tag.id),
    );
  }, [allTags, assignedTags]);

  const showNewTag =
    trimmedSearchText.length > 0 &&
    !allTags.find(
      (tag: any) => tag.text.toLowerCase() === trimmedSearchText.toLowerCase(),
    );

  const handleOutsideClick = (event: any) => {
    event.stopPropagation();
    const hasDropdownRef =
      tagsDropdownRef.current &&
      !tagsDropdownRef.current.contains(event.target);
    const hasBtnRef = btnRef.current && btnRef.current.contains(event.target);
    if (hasDropdownRef && !hasBtnRef) toggleShowTags(event);
  };

  const dispatchAssignedTags = (tags: FilterOption[]) => {
    const setterFunc =
      caller === "floatingBar" ? setTemporaryAssignedTags : setAssignedTags;

    dispatch(setterFunc(tags));
  };

  const handleTagAddition = async (selectedTag: FilterOption) => {
    setCurrentSelectedTags((prev) => {
      const exists = prev.some((tag) => tag.id === selectedTag.id);
      if (exists) {
        return prev.filter((tag) => tag.id !== selectedTag.id);
      } else {
        return [...prev, { ...selectedTag, selected: true }];
      }
    });
    await dispatch(updateExportTags(datasetID, selectedTag) as any);
  };

  const handleCloseSearch = () => {
    setSearchText("");
    setSearchedResults(nonAssignedTags);
  };

  const handleTagAssignment = () => {
    onTagSubmission(currentSelectedTags.map((item: FilterOption) => item.id));
    setCurrentSelectedTags([]);
    const updatedTags = [...assignedTags, ...currentSelectedTags];
    dispatchAssignedTags(updatedTags);
  };

  const handleNewTagClick = async (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    handleCloseSearch();
    const tagID = await dispatch(addNewTagFromCart(datasetID, newTag) as any);
    if (tagID) {
      await handleTagAddition({ id: tagID, text: newTag });
    }
  };

  const handleTagRemoval = async (selectedTag: FilterOption) => {
    const updatedTags = assignedTags.map((tag) =>
      tag.id === selectedTag.id ? { ...tag, selected: false } : tag,
    );
    dispatchAssignedTags(updatedTags);
    onTagRemoval(selectedTag.id);
  };

  const renderInputSection = () => {
    return (
      <div
        className={styles.searchBarContainer}
        onClick={(e: any) => e.stopPropagation()}
      >
        <div className={styles.searchBarButton}>
          <SearchIcon />
        </div>
        <input
          type="search"
          onChange={(e: any) => setSearchText(e.target.value)}
          value={searchText}
          className={styles.searchBarInput}
          placeholder="Search or create a new tag."
        />
        {searchText && (
          <div className={styles.clearSearchIcon} onClick={handleCloseSearch}>
            <XIcon variant="new" />
          </div>
        )}
      </div>
    );
  };

  const renderAssignedTags = () => {
    if (assignedTags.length > 0) {
      return [
        <div className={styles.assignedTagsContainer}>
          {assignedTags.map((tag: FilterOption, index: number) => (
            <SingleTagTag
              key={index}
              tag={tag}
              handleRemoval={handleTagRemoval}
            />
          ))}
        </div>,
        <div className={styles.divider}></div>,
      ];
    }
  };

  const renderTagsSelection = () => {
    if (searchedResults.length > 0) {
      return (
        <div className={styles.availableTags}>
          {searchedResults.map((tag: FilterOption) => {
            const isSelected = currentSelectedTags.some(
              (item: FilterOption) => item.id === tag.id,
            );

            return (
              <SingleTagRow
                key={tag.id}
                tag={tag}
                selected={isSelected}
                onTagAddition={handleTagAddition}
              />
            );
          })}
        </div>
      );
    }
  };

  const showNewTagOption = () => {
    const newOptionClassnames = classNames(styles.newOptionText, {
      [styles.borderTop]: searchedResults.length > 0,
    });
    if (showNewTag) {
      return (
        <div className={newOptionClassnames} onClick={handleNewTagClick}>
          <FaPlus />
          Create a new tag <strong>{`"${newTag}"`}</strong>
        </div>
      );
    } else return null;
  };

  useEffect(() => {
    if (trimmedSearchText.length > 0) {
      setSearchedResults([
        ...nonAssignedTags.filter((tag: any) =>
          tag.text.toLowerCase().includes(trimmedSearchText.toLowerCase()),
        ),
        ...assignedTags.filter((tag: any) =>
          tag.text.toLowerCase().includes(trimmedSearchText.toLowerCase()),
        ),
      ]);
    } else {
      setSearchedResults(nonAssignedTags);
    }

    if (showNewTag) setNewTag(trimmedSearchText);

    //eslint-disable-next-line
  }, [searchText, nonAssignedTags, assignedTags]);

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
    //eslint-disable-next-line
  }, []);

  return (
    <div
      ref={tagsDropdownRef}
      onClick={(e) => e.stopPropagation()}
      className={styles.tagsDropDownWrapper}
    >
      <div className={styles.dropdownHeader}>
        <span>Manage Tags</span>
        <div className={styles.crossIcon} onClick={toggleShowTags}>
          <XIcon variant="new" />
        </div>
      </div>
      <div className={styles.divider}></div>
      {renderAssignedTags()}
      {renderInputSection()}
      <div className={tagsContainerStyle}>
        {renderTagsSelection()}
        {showNewTagOption()}
      </div>
      {showApplybutton && (
        <div className={styles.applyTagContainer}>
          <VLButton
            type="primary"
            text="Apply"
            className={styles.applyTagButton}
            onClick={handleTagAssignment}
          />
        </div>
      )}
    </div>
  );
};
export default TagsDropDown;

interface SingleTagProps {
  tag: FilterOption;
  selected: boolean;
  onTagAddition: (selectedTag: FilterOption) => void;
}

interface SingleTagTagProps {
  tag: FilterOption;
  handleRemoval: (tag: FilterOption) => void;
}

type Caller = "cart" | "floatingBar";

interface TagsDropDownProps {
  assignedTags: FilterOption[];
  allTags: FilterOption[];
  datasetID: string;
  caller?: Caller;
  onTagRemoval: (tagID: number) => void;
  onTagSubmission: (tagIDs: number[]) => void;
  toggleShowTags: (e: React.MouseEvent<HTMLDivElement>) => void;
  btnRef: RefObject<HTMLDivElement>;
}
